import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, IconButton, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import BlueprintPresentationTable from '../../../../../../organisms/QueryBlueprint/BlueprintPresentationTable'
import BlueprintDataGrid from '../../../../../../organisms/QueryBlueprint/BlueprintDataGrid'
import BlueprintMetric from '../../../../../../organisms/QueryBlueprint/BlueprintMetric'
import BlueprintChart from '../../../../../../organisms/QueryBlueprint/BlueprintChart'
import BlueprintHierarchicalTable from '../../../../../../organisms/QueryBlueprint/BlueprintHierarchicalTable'
import BlueprintHierarchicalGrid from '../../../../../../organisms/QueryBlueprint/BlueprintHierarchicalGrid'
import BlueprintScopeSelector from '../../../../../../organisms/QueryBlueprint/BlueprintScopeSelector'
import BlueprintHierarchicalPivotTable from '../../../../../../organisms/QueryBlueprint/BlueprintHierarchicalPivotTable'
import SydButton from '../../../../../../commonDesign/Button'

const RENDERERS = {
  'Presentation Table': BlueprintPresentationTable,
  'Data Grid': BlueprintDataGrid,
  Metric: BlueprintMetric,
  Chart: BlueprintChart,
  'Hierarchical Table': BlueprintHierarchicalTable,
  'Hierarchical Grid': BlueprintHierarchicalGrid,
  'Scope Selector': BlueprintScopeSelector,
  'Hierarchical Pivot Table': BlueprintHierarchicalPivotTable
}

const LATCH_LATEST_SENTINEL = '__latest'

const useStyles = makeStyles(() => ({
  paper: {
    minWidth: '900px',
    maxWidth: '95vw',
    width: '95vw',
    height: '85vh',
    maxHeight: '85vh',
    margin: 0
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 18px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  meta: {
    fontFamily: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace",
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  paramBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px 18px',
    backgroundColor: 'var(--color-neutral-025, #fafafa)',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  paramHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  paramHeaderHint: {
    fontSize: '11px',
    fontWeight: 400,
    color: 'var(--color-neutral-500)',
    textTransform: 'none',
    letterSpacing: 'normal'
  },
  paramGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '10px'
  },
  paramField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  paramLabel: {
    fontFamily: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace",
    fontSize: '11px',
    color: 'var(--color-neutral-700)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  paramLabelTag: {
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--color-neutral-500)',
    backgroundColor: 'var(--color-neutral-100)',
    padding: '1px 5px',
    borderRadius: '3px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  paramInput: {
    height: '28px',
    padding: '0 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'inherit',
    color: 'var(--color-neutral-900)',
    backgroundColor: 'white',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--color-primary-500)'
    }
  },
  paramActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '18px',
    backgroundColor: 'white'
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  error: {
    padding: '24px',
    color: 'var(--color-danger-500)',
    fontSize: '13px'
  },
  emptyParamsHint: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic'
  }
}))

// Hierarchical configs reference purposes via levels / footers / scope-selector
// arrays, so a flat `option.config.purposeCode` is not sufficient.
function collectPurposeCodes (option) {
  const codes = new Set()
  const cfg = option?.config
  if (!cfg) return []
  if (typeof cfg.purposeCode === 'string') codes.add(cfg.purposeCode)
  if (typeof cfg.footerPurposeCode === 'string') codes.add(cfg.footerPurposeCode)
  const levels = cfg.hierarchy?.levels
  if (Array.isArray(levels)) {
    for (const level of levels) {
      if (typeof level?.purposeCode === 'string') codes.add(level.purposeCode)
    }
  }
  const purposes = cfg.purposes
  if (Array.isArray(purposes)) {
    for (const p of purposes) {
      if (typeof p?.purposeCode === 'string') codes.add(p.purposeCode)
    }
  }
  return Array.from(codes)
}

function discoverPreviewParameters (draft, purposeCodes) {
  const byName = new Map()

  for (const code of purposeCodes) {
    const purpose = draft?.data?.purposes?.[code]
    if (!purpose) continue

    const required = purpose.masterPredicate?.requiredParams || []
    for (const name of required) {
      if (typeof name !== 'string' || !name) continue
      if (!byName.has(name)) {
        byName.set(name, { name, kind: 'required', sources: [code] })
      } else {
        byName.get(name).sources.push(code)
      }
    }

    const latch = purpose.latch
    if (latch?.parameterName) {
      const name = latch.parameterName
      const defaultValue = latch.defaultBehavior === 'latest' ? LATCH_LATEST_SENTINEL : ''
      if (!byName.has(name)) {
        byName.set(name, {
          name,
          kind: 'latch',
          sources: [code],
          defaultBehavior: latch.defaultBehavior,
          defaultValue
        })
      } else {
        const existing = byName.get(name)
        existing.sources.push(code)
        if (existing.kind !== 'latch') {
          existing.kind = 'latch'
          existing.defaultBehavior = latch.defaultBehavior
          existing.defaultValue = defaultValue
        }
      }
    }
  }

  return Array.from(byName.values())
}

function PreviewModal ({ open, onClose, draft, optionCode, blueprintCode, previewLabel }) {
  const classes = useStyles()

  const option = optionCode ? draft?.presentation?.options?.[optionCode] : null
  const purposeCodes = useMemo(() => collectPurposeCodes(option), [option])
  const primaryPurposeCode = option?.config?.purposeCode ??
    option?.config?.hierarchy?.levels?.[0]?.purposeCode
  const primaryPurpose = primaryPurposeCode ? draft?.data?.purposes?.[primaryPurposeCode] : null

  const Renderer = option?.type ? RENDERERS[option.type] : null

  // Draft is passed as inline `definition` so unsaved edits hit debug-query-blueprint.
  const inlineDefinition = draft?.data ?? null

  const discoveredParams = useMemo(
    () => discoverPreviewParameters(draft, purposeCodes),
    [draft, purposeCodes]
  )

  // formValues = typed inputs; appliedValues = what the renderer reads (Run promotes one to the other).
  const [formValues, setFormValues] = useState({})
  const [appliedValues, setAppliedValues] = useState({})

  useEffect(() => {
    const seed = {}
    for (const p of discoveredParams) {
      seed[p.name] = p.kind === 'latch' && p.defaultValue ? p.defaultValue : ''
    }
    setFormValues(seed)
    setAppliedValues(seed)
  }, [discoveredParams])

  const handleFieldChange = useCallback((name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }))
  }, [])

  const handleRun = useCallback(() => {
    setAppliedValues(formValues)
  }, [formValues])

  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(formValues), ...Object.keys(appliedValues)])
    for (const key of keys) {
      if ((formValues[key] ?? '') !== (appliedValues[key] ?? '')) return true
    }
    return false
  }, [formValues, appliedValues])

  // Empty strings are dropped so missingness is expressed as "absent".
  const cleanParameters = useMemo(() => {
    const out = {}
    for (const [k, v] of Object.entries(appliedValues)) {
      if (v !== '' && v != null) out[k] = v
    }
    return out
  }, [appliedValues])

  const rendererProps = useMemo(() => {
    if (!option || !Renderer) return null
    return {
      blueprintCode,
      config: option.config,
      displayName: option.displayName,
      description: option.description,
      subordinatePredicates: primaryPurpose?.subordinatePredicates ?? [],
      latch: primaryPurpose?.latch ?? null,
      purposeColumns: primaryPurpose?.columns ?? [],
      parameters: cleanParameters,
      scopePredicates: [],
      definition: inlineDefinition
    }
  }, [Renderer, option, primaryPurpose, blueprintCode, inlineDefinition, cleanParameters])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <div className={classes.title}>
            Preview · {option?.displayName || optionCode}
          </div>
          <div className={classes.meta}>
            {option?.type} · {optionCode} · {blueprintCode}
            {previewLabel ? ` · ${previewLabel}` : ''}
          </div>
        </div>
        <IconButton size='small' onClick={onClose} aria-label='Close preview'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </div>

      {option && (
        <div className={classes.paramBar}>
          <div className={classes.paramHeader}>
            <span>Parameters</span>
            <span className={classes.paramHeaderHint}>
              {discoveredParams.length === 0
                ? 'This option has no required parameters.'
                : 'Fill in values then click Run to refresh the preview.'}
            </span>
          </div>
          {discoveredParams.length > 0 && (
            <>
              <div className={classes.paramGrid}>
                {discoveredParams.map(p => (
                  <div key={p.name} className={classes.paramField}>
                    <label className={classes.paramLabel} htmlFor={`preview-param-${p.name}`}>
                      <span>{p.name}</span>
                      {p.kind === 'latch' && (
                        <span className={classes.paramLabelTag} title={`Latch param (defaultBehavior: ${p.defaultBehavior || 'required'})`}>
                          latch
                        </span>
                      )}
                    </label>
                    <input
                      id={`preview-param-${p.name}`}
                      type='text'
                      className={classes.paramInput}
                      value={formValues[p.name] ?? ''}
                      placeholder={p.kind === 'latch' ? LATCH_LATEST_SENTINEL : `Enter ${p.name}`}
                      onChange={(e) => handleFieldChange(p.name, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <div className={classes.paramActions}>
                <SydButton
                  size='sm'
                  variant='primary'
                  onClick={handleRun}
                  disabled={!isDirty}
                >
                  Run
                </SydButton>
              </div>
            </>
          )}
        </div>
      )}

      <DialogContent className={classes.dialogContent}>
        <div className={classes.body}>
          {!option && (
            <div className={classes.error}>
              No presentation option named &quot;{optionCode}&quot; in the draft.
            </div>
          )}
          {option && !Renderer && (
            <div className={classes.error}>
              Unknown presentation type &quot;{option.type}&quot;.
            </div>
          )}
          {option?.type === 'Scope Selector' && (
            <div className={classes.error}>
              Scope Selector previews aren&apos;t available here. The picker reads from a live
              page-level scope context provided by the host view. Verify on the page that
              embeds this blueprint.
            </div>
          )}
          {option && Renderer && option.type !== 'Scope Selector' && rendererProps && inlineDefinition && (
            <Renderer {...rendererProps} />
          )}
          {option && Renderer && option.type !== 'Scope Selector' && !inlineDefinition && (
            <div className={classes.error}>
              No data definition available to preview.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

PreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  draft: PropTypes.object,
  optionCode: PropTypes.string,
  blueprintCode: PropTypes.string,
  previewLabel: PropTypes.string
}

export default PreviewModal
